// services/trackingService.js
import cookieService from './cookieService';
import { ref, set, update, push } from 'firebase/database';
import { db } from './firebaseConfig';

export class TrackingService {
  constructor() {
    this.userId = this.getOrCreateUserId();
    this.influencerId = this.getInfluencerId();
    this.localActivities = []; // Store activities locally to avoid DB reads
  }

  async track(event, data = {}) {
    try {
      const eventData = {
        event,
        userId: this.userId,
        influencerId: this.influencerId,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        ...data
      };

      // Update user record FIRST (single source of truth)
      await this.updateUserRecord(event, data);
      
      // Then save to events collection (for detailed history)
      await this.saveToEvents(eventData);
      
      console.log(`🎯 Tracked: ${event}`);
      
      return { success: true, event: event };
      
    } catch (error) {
      console.error('Tracking error:', error);
      this.queueFailedEvent(event, data);
      return { success: false, error: error.message };
    }
  }

removeUndefinedValues(obj) {
  const clean = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      clean[key] = obj[key];
    }
  });
  return clean;
}
  // Update single user record - SIMPLIFIED to avoid loops
  async updateUserRecord(event, data) {
    const userRef = ref(db, `/users/${this.userId}`);
    const timestamp = new Date().toISOString();

    // Build the activity item
    const newActivity = {
      timestamp,
      event,
      ...data
    };

    // Update local activities array (keep last 20)
    this.localActivities = [...this.localActivities, newActivity].slice(-20);

    // Prepare updates for user record
    const updates = {
      userId: this.userId,
      lastUpdated: timestamp,
      lastEvent: event,
      userAgent: navigator.userAgent,
      lastUrl: window.location.href
    };

    // CRITICAL FIX: Always preserve influencerId to prevent counter countdown
    if (this.influencerId) {
      updates.influencerId = this.influencerId;
    }

    // Store activity in a separate path to avoid overwriting the array
    // This prevents losing activities on page refresh
    const activityRef = ref(db, `/users/${this.userId}/activities/${timestamp.replace(/[:.]/g, '-')}`);
    set(activityRef, newActivity).catch(err => console.warn('Failed to save activity:', err));

    // Add event-specific fields
    switch (event) {
      case 'influencer_visit':
        updates.influencerId = data.influencerId;
        updates.firstVisit = timestamp;
        updates.courseId = data.courseId;
        if (!updates.createdAt) {
          updates.createdAt = timestamp;
        }
        break;
        
      case 'profile_update':
      case 'course_registration':
        if (data.name) updates.name = data.name;
        if (data.email) updates.email = data.email;
        if (data.phone) updates.phone = data.phone;
        updates.registrationDate = timestamp;
        updates.registrationCourseId = data.courseId;
        break;
        
      case 'course_enrollment':
        updates.enrollmentDate = timestamp;
        updates.enrollmentCourseId = data.courseId;
        if (data.amount) updates.enrollmentAmount = data.amount;
        break;
        
      case 'form_submission':
        updates.lastFormSubmission = timestamp;
        updates.lastFormType = data.formType;
        break;
    }
    const cleanUpdates = this.removeUndefinedValues(updates);

    // CRITICAL FIX: Use update() instead of set() to merge data, not replace
    // This prevents losing influencerId and other fields
    await update(userRef, cleanUpdates);
  }

  // Save to events collection (separate from user record)
  async saveToEvents(eventData) {
    try {
      const eventsRef = ref(db, 'events');
      const newEventRef = push(eventsRef);
      await set(newEventRef, eventData);
    } catch (error) {
      console.warn('Failed to save to events collection:', error);
      // Don't throw - events collection is optional
    }
  }

  setInfluencer(influencerId) {
    if (influencerId) {
      this.influencerId = influencerId;
      cookieService.setCookie('influencer_id', influencerId, 30);
      try {
        localStorage.setItem('influencer_id', influencerId);
      } catch (e) {
        console.warn('localStorage not available for influencer ID');
      }
    }
  }

  async updateProfile(userData) {
    return this.track('profile_update', userData);
  }

  // Queue failed events for retry
  queueFailedEvent(event, data) {
    try {
      const failedEvents = JSON.parse(localStorage.getItem('failed_events') || '[]');
      failedEvents.push({ 
        event, 
        data, 
        timestamp: new Date().toISOString(),
        userId: this.userId 
      });
      localStorage.setItem('failed_events', JSON.stringify(failedEvents));
    } catch (e) {
      console.warn('Failed to queue event for retry');
    }
  }

  // Retry failed events
  async retryFailedEvents() {
    try {
      const failedEvents = JSON.parse(localStorage.getItem('failed_events') || '[]');
      
      if (failedEvents.length === 0) return;
      
      console.log(`Retrying ${failedEvents.length} failed events...`);
      
      for (const failedEvent of failedEvents) {
        try {
          await this.track(failedEvent.event, failedEvent.data);
        } catch (error) {
          console.error('Failed to retry event:', failedEvent.event, error);
        }
      }
      
      // Clear successfully retried events
      localStorage.removeItem('failed_events');
      console.log('Failed events retry completed');
      
    } catch (error) {
      console.error('Retry failed:', error);
    }
  }

  // User ID management
  getOrCreateUserId() {
    let userId = cookieService.getCookie('user_id');
    
    if (!userId) {
      try {
        userId = localStorage.getItem('user_id');
      } catch (e) {
        // localStorage not available
      }
      
      if (!userId) {
        userId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        cookieService.setCookie('user_id', userId, 365);
        try {
          localStorage.setItem('user_id', userId);
        } catch (e) {
          console.warn('localStorage not available for user ID');
        }
      }
    }
    
    return userId;
  }

  getInfluencerId() {
    return cookieService.getCookie('influencer_id') || 
           (typeof localStorage !== 'undefined' ? localStorage.getItem('influencer_id') : null);
  }

  // Getters
  getUserId() { return this.userId; }
  getCurrentInfluencerId() { return this.influencerId; }
}

// Singleton instance
export default new TrackingService();